<template>
  <v-form @submit.prevent>
    <v-layout
      row
      wrap
      :class="{
        'd-flex': $vuetify.breakpoint.lgAndUp,
        'pb-4': $vuetify.breakpoint.lgAndUp
      }"
      v-bind="{ 'justify-center': $vuetify.breakpoint.mdAndDown }"
    >
      <v-flex
        xs12
        sm7
        md6
        lg4
        :class="{ 'pr-4': $vuetify.breakpoint.lgAndUp }"
        :style="{ 'min-width': $vuetify.breakpoint.lgAndUp ? '440px' : '' }"
      >
        <connect-vc-share-comic-thumbnail-my-contents
          :lang="lang.ComicThumbnail"
        >
        </connect-vc-share-comic-thumbnail-my-contents>

        <template v-if="initialPublicationType !== 'secure_view'">
          <connect-vc-share-social-media-image-my-contents
            :lang="lang.ComicThumbnail"
          >
          </connect-vc-share-social-media-image-my-contents>
        </template>
      </v-flex>
      <v-flex
        xs12
        sm12
        md12
        lg8
        :class="{ 'pl-4': $vuetify.breakpoint.lgAndUp }"
      >
        <p :class="[$style.headingText]">
          {{ lang.MyContents.title + lang.MyContents.required }}
        </p>
        <v-text-field
          v-model="title"
          outline
          single-line
          counter="50"
          v-validate="'required|max:50'"
          data-vv-name="contentsTitle"
          :error-messages="errors.collect('contentsTitle')"
          required
        ></v-text-field>
        <p :class="[$style.headingText]">
          {{ lang.MyContents.description + lang.MyContents.required }}
        </p>
        <v-textarea
          v-model="description"
          no-resize
          outline
          single-line
          counter="200"
          v-validate="'required|max:200'"
          data-vv-name="contentsDescription"
          :error-messages="errors.collect('contentsDescription')"
          required
        ></v-textarea>
      </v-flex>
    </v-layout>

    <template v-if="initialPublicationType !== 'secure_view'">
      <p :class="[$style.headingText]">
        {{ lang.MyContents.publication_type }}
      </p>
      <v-radio-group v-model="publicationType" row>
        <v-radio
          :label="lang.MyContents.publication_type_public"
          value="public"
          v-on:change="changeSecretWord = false"
        ></v-radio>
        <v-radio
          :label="lang.MyContents.publication_type_secret_word"
          value="secret_word"
          v-on:change="
            initialPublicationType !== 'secret_word'
              ? (changeSecretWord = true)
              : ''
          "
        ></v-radio>
      </v-radio-group>

      <v-fade-transition leave-absolute>
        <v-layout row wrap v-if="publicationType === 'secret_word'">
          <v-flex v-if="initialPublicationType !== 'secret_word'" xs12>
            <v-card class="mt-3" max-width="500">
              <v-card-title class="pb-0 title">
                <div>{{ lang.MyContents.secret_word }}</div>
              </v-card-title>
              <v-card-text class="pt-0">
                <v-text-field
                  class="pt-2"
                  background-color="amber lighten-4"
                  v-model="secretWord"
                  box
                  single-line
                  type="password"
                  v-validate="changeSecretWord ? 'required' : ''"
                  data-vv-name="contentsSecretWord"
                  :error-messages="errors.collect('contentsSecretWord')"
                  required
                >
                  {{ secretWord }}
                </v-text-field>
              </v-card-text>
            </v-card>
          </v-flex>

          <v-flex v-else-if="initialPublicationType === 'secret_word'" xs12>
            <v-btn
              class="ml-0"
              large
              round
              @click="changeSecretWord = true"
              :class="[
                $style.btnTextStyle,
                { 'grey lighten-1': changeSecretWord }
              ]"
            >
              <v-icon>fas fa-lock fa-fw</v-icon>
              <span class="pl-2">{{ lang.MyContents.update_secret_word }}</span>
            </v-btn>
            <v-card class="mt-3" v-show="changeSecretWord" max-width="500">
              <v-card-title class="pb-0 title">
                <div>{{ lang.MyContents.new_secret_word }}</div>
                <v-spacer></v-spacer>
                <div class="text-xs-right-">
                  <v-btn icon @click="changeSecretWord = !changeSecretWord">
                    <v-icon small>fas fa-times</v-icon>
                  </v-btn>
                </div>
              </v-card-title>
              <v-card-text class="pt-0">
                <v-text-field
                  class="pt-2"
                  background-color="amber lighten-4"
                  v-model="secretWord"
                  box
                  single-line
                  type="password"
                  v-validate="changeSecretWord ? 'required' : ''"
                  data-vv-name="contentsSecretWord"
                  :error-messages="errors.collect('contentsSecretWord')"
                  required
                >
                  {{ secretWord }}
                </v-text-field>
              </v-card-text>
            </v-card>
          </v-flex>
        </v-layout>
      </v-fade-transition>

      <v-checkbox
        v-model="isSensitive"
        :label="lang.MyContents.is_sensitive"
        :hint="
          '<div class=\'pl-4 body-2\' style=\'line-height: 1.5em\'>' +
            sensitiveHint +
            '</div>'
        "
        persistent-hint
        class="mb-2 d-block"
      ></v-checkbox>
      <v-card
        class="text-xs-center body-2 orange darken-2 pa-1 white--text"
        flat
        max-width="60"
      >
        {{ lang.MyContents.hint_card_text }}
      </v-card>
      <ul class="ml-4 pb-4 pt-1 body-2">
        <li v-html="sensitiveHintText"></li>
      </ul>

      <p :class="[$style.headingText]">
        {{ lang.MyContents.is_comment_enabled }}
      </p>
      <v-radio-group v-model="isCommentEnabled" row>
        <v-radio
          :label="lang.MyContents.is_comment_enabled_accept"
          :value="1"
        ></v-radio>
        <v-radio
          :label="lang.MyContents.is_comment_enabled_not_accept"
          :value="0"
        ></v-radio>
      </v-radio-group>
    </template>

    <div class="text-xs-right">
      <v-btn
        color="info"
        @click="callSaveChanges"
        :class="$style.btnTextStyle"
        :loading="isLoading"
        :disabled="
          errors.has('contentsTitle') ||
            errors.has('contentsDescription') ||
            errors.has('contentsSecretWord')
        "
      >
        {{ lang.MyContents.save }}
      </v-btn>
    </div>
  </v-form>
</template>

<script>
import ice from "../../../lib/ice";

export default {
  name: "vc-share-my-contents-settings",
  props: {
    locale: {
      type: String,
      default: ""
    },
    lang: {
      Type: Object,
      default: () => {
        return {
          MyContents: {
            title: "タイトル",
            description: "せつめい",
            required: "（必須）",
            publication_type: "公開範囲を設定する",
            publication_type_public: "誰でも閲覧可能",
            publication_type_secret_word: "合言葉を知っている人だけ",
            secret_word: "合言葉",
            new_secret_word: "新しい合言葉",
            is_comment_enabled: "コメント投稿",
            is_comment_enabled_accept: "受け付ける",
            is_comment_enabled_not_accept: "受け付けない",
            is_sensitive: "自主規制を設定する",
            is_sensitive_note:
              "例）性的、残酷、グロテスク表現など（ :a_start レーティング基準表 :a_end を参考に18歳未満の青少年に見せてよいかどうかを基準に判断してください）",
            sensitive_hint_text:
              "設定すると、公開時に :a_start サムネイル・作品情報など :a_end が非表示になります。",
            hint_card_text: "ヒント"
          },
          ComicThumbnail: {}
        };
      }
    },
    validationLang: {
      type: Object,
      default: () => {
        return {
          required: "",
          max: {
            string: ""
          }
        };
      }
    },
    localeCode: {
      type: String,
      default: ""
    },
    contentsId: { type: Number, default: 0 },
    initialTitle: { type: String, default: "" },
    initialDescription: { type: String, default: "" },
    initialPublicationType: { type: String, default: "" },
    saveSettingsChanges: {
      type: Function,
      default: () => {}
    },
    initialIsSensitive: {
      type: Number,
      default: 0
    },
    initialIsCommentEnabled: {
      type: Number,
      default: 0
    },
    ratingCriteriaTableUrl: {
      type: String,
      default: ""
    },
    contentsThumbnailUrl: {
      type: String,
      default: ""
    },
    setMyContentsThumbnailImageUrl: {
      type: Function,
      default: () => {}
    },
    identificationCode: {
      type: String,
      default: ""
    },
    setContentsIdentificationCode: {
      type: Function,
      default: () => {}
    },
    updatedThumbnailIdentificationCode: {
      type: String,
      default: ""
    },
    userThumbnailCroppedImageUrl: {
      type: String,
      default: ""
    },
    socialMediaImageUrl: {
      type: String,
      default: ""
    },
    setSocialMediaImageUrl: {
      type: Function,
      default: () => {}
    },
    updatedSocialMediaImageIdentificationCode: {
      type: String,
      default: ""
    },
    userCroppedSocialMediaImageUrl: {
      type: String,
      default: ""
    }
  },
  data: function() {
    return {
      isIce: "",
      title: this.initialTitle,
      description: this.initialDescription,
      publicationType: this.initialPublicationType,
      secretWord: "",
      errorMessage: "",
      isLoading: false,
      changeSecretWord: false,
      dictionary: {
        custom: {
          contentsTitle: {},
          contentsDescription: {},
          contentsSecretWord: {}
        }
      },
      isCommentEnabled: this.initialIsCommentEnabled,
      isSensitive: this.initialIsSensitive,
      sensitiveHint: "",
      sensitiveHintText: "",
      sensitiveHintDom: "",
      sensitiveHintTextDom: ""
    };
  },
  created: function() {
    this.isIce = ice.isIce();
    this.sensitiveHint = this.lang.MyContents.is_sensitive_note
      .replace(
        ":a_start",
        '<a href="' +
          this.ratingCriteriaTableUrl +
          '" id="sensitiveHintDom" target="' +
          (this.isIce ? "_blank" : "_self") +
          '">'
      )
      .replace(":a_end", "</a>");
    this.sensitiveHintText = this.lang.MyContents.sensitive_hint_text
      .replace(
        ":a_start",
        '<a href="/' +
          this.locale +
          '/help#about-sensitive-contents" id="sensitiveHintTextDom" target="' +
          (this.isIce ? "_blank" : "_self") +
          '">'
      )
      .replace(":a_end", "</a>");
  },
  mounted: function() {
    this.dictionary.custom.contentsTitle.max = this.validationLang.max.string
      .replace(":attribute", this.lang.MyContents.title)
      .replace(":max", "50");
    this.dictionary.custom.contentsTitle.required = this.validationLang.required.replace(
      ":attribute",
      this.lang.MyContents.title
    );
    this.dictionary.custom.contentsDescription.max = this.validationLang.max.string
      .replace(":attribute", this.lang.MyContents.description)
      .replace(":max", "200");
    this.dictionary.custom.contentsDescription.required = this.validationLang.required.replace(
      ":attribute",
      this.lang.MyContents.description
    );
    this.dictionary.custom.contentsSecretWord.required = this.validationLang.required.replace(
      ":attribute",
      this.lang.MyContents.secret_word
    );
    this.$validator.localize(this.localeCode, this.dictionary);
    this.setMyContentsThumbnailImageUrl(this.contentsThumbnailUrl);
    this.setSocialMediaImageUrl(this.socialMediaImageUrl);
    this.setContentsIdentificationCode(this.identificationCode);

    if (this.initialPublicationType !== "secure_view") {
      this.sensitiveHintDom = document.getElementById("sensitiveHintDom");
      this.sensitiveHintDom.addEventListener("click", this.onClick, false);
      this.sensitiveHintTextDom = document.getElementById(
        "sensitiveHintTextDom"
      );
      this.sensitiveHintTextDom.addEventListener("click", this.onClick, false);
    }
  },
  methods: {
    callSaveChanges: function() {
      this.$validator.validateAll().then(result => {
        if (!result) {
          return;
        }

        this.isLoading = true;

        let requestData = {
          contentsId: this.contentsId,
          title: this.title,
          description: this.description
        };

        if (this.initialPublicationType !== "secure_view") {
          requestData.publicationType = this.publicationType;
          requestData.isSensitive = this.isSensitive;
          requestData.isCommentEnabled = this.isCommentEnabled;

          if (this.changeSecretWord) {
            requestData.secretWord = this.secretWord;
          }
        }

        if (this.userThumbnailCroppedImageUrl !== "") {
          requestData.updatedThumbnailIdentificationCode = this.updatedThumbnailIdentificationCode;
        }

        if (this.userCroppedSocialMediaImageUrl !== "") {
          requestData.updatedSocialMediaImageIdentificationCode = this.updatedSocialMediaImageIdentificationCode;
        }

        this.saveSettingsChanges(requestData).then(value => {
          if (value === 0) {
            location.reload();
          } else {
            this.isLoading = false;
          }
        });
      });
    },
    onClick(event) {
      ice.onClickLink(event);
    }
  }
};
</script>

<style module>
.btnTextStyle {
  text-transform: none !important;
}
.headingText {
  font-weight: bold;
  font-size: 16px;
}
</style>
